import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Stack, Divider,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon, RadioButtonUnchecked, Autorenew,
} from '@mui/icons-material';
import { roadmap, ROADMAP_STAND, RoadmapItem, RoadmapStatus, RoadmapPrio } from '../data/roadmap';

const statusMeta: Record<RoadmapStatus, { label: string; color: string; icon: React.ReactNode }> = {
  offen:     { label: 'Offen',     color: '#1565c0', icon: <RadioButtonUnchecked sx={{ fontSize: 14 }} /> },
  in_arbeit: { label: 'In Arbeit', color: '#e65100', icon: <Autorenew sx={{ fontSize: 14 }} /> },
  idee:      { label: 'Idee',      color: '#6a1b9a', icon: <LightbulbIcon sx={{ fontSize: 14 }} /> },
};

const prioMeta: Record<RoadmapPrio, { label: string; color: string }> = {
  hoch:    { label: 'Prio hoch',    color: '#c62828' },
  mittel:  { label: 'Prio mittel',  color: '#e65100' },
  niedrig: { label: 'Prio niedrig', color: '#78909c' },
};

const aufwandLabel: Record<string, string> = { S: 'Aufwand S', M: 'Aufwand M', L: 'Aufwand L' };

function ItemRow({ item }: { item: RoadmapItem }) {
  const s = statusMeta[item.status];
  return (
    <Box sx={{ py: 1.25 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: item.description ? 0.5 : 0 }}>
        <Typography variant="body2" fontWeight={700} sx={{ mr: 0.5 }}>{item.title}</Typography>
        <Chip size="small" icon={s.icon as any} label={s.label}
          sx={{ height: 20, fontSize: '0.68rem', color: s.color, borderColor: s.color, '& .MuiChip-icon': { color: s.color } }}
          variant="outlined" />
        <Chip size="small" label={prioMeta[item.prio].label}
          sx={{ height: 20, fontSize: '0.68rem', bgcolor: prioMeta[item.prio].color, color: 'white' }} />
        <Chip size="small" label={aufwandLabel[item.aufwand]} variant="outlined"
          sx={{ height: 20, fontSize: '0.68rem', color: 'text.secondary' }} />
      </Box>
      {item.description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.description}</Typography>
      )}
    </Box>
  );
}

export default function Roadmap() {
  const [filter, setFilter] = useState<'alle' | RoadmapStatus>('alle');

  const categories = roadmap
    .map(cat => ({ ...cat, items: filter === 'alle' ? cat.items : cat.items.filter(i => i.status === filter) }))
    .filter(cat => cat.items.length > 0);

  const total = roadmap.reduce((n, c) => n + c.items.length, 0);
  const offen = roadmap.reduce((n, c) => n + c.items.filter(i => i.status === 'offen').length, 0);
  const ideen = roadmap.reduce((n, c) => n + c.items.filter(i => i.status === 'idee').length, 0);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Offene Punkte und Ideen zur Weiterentwicklung · Stand {ROADMAP_STAND} · {total} Einträge ({offen} offen, {ideen} Ideen)
      </Typography>

      {/* Filter */}
      <ToggleButtonGroup value={filter} exclusive size="small" sx={{ mb: 2 }}
        onChange={(_, v) => { if (v) setFilter(v); }}>
        <ToggleButton value="alle" sx={{ textTransform: 'none', px: 2 }}>Alle</ToggleButton>
        <ToggleButton value="offen" sx={{ textTransform: 'none', px: 2 }}>Offen</ToggleButton>
        <ToggleButton value="in_arbeit" sx={{ textTransform: 'none', px: 2 }}>In Arbeit</ToggleButton>
        <ToggleButton value="idee" sx={{ textTransform: 'none', px: 2 }}>Ideen</ToggleButton>
      </ToggleButtonGroup>

      {/* Kategorien */}
      <Stack spacing={2}>
        {categories.map(cat => (
          <Card key={cat.id} elevation={1}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#4a7c59', mb: 0.5 }}>
                {cat.label}
              </Typography>
              <Divider />
              {cat.items.map((item, i) => (
                <React.Fragment key={item.title}>
                  <ItemRow item={item} />
                  {i < cat.items.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
        Vorschläge und Prioritäten sind ein Arbeitsstand – Rückmeldungen jederzeit willkommen.
      </Typography>
    </Box>
  );
}
